import { Bot, ChevronDown } from "lucide-react";
import { useId, useState } from "react";
import { channelPhrase, displayName, timeRange } from "@/lib/history";
import type { HistoryEvent } from "@/lib/types";
import { cn } from "@/lib/utils";
import { Avatar, HistoryEventRow } from "./HistoryEventRow";

const card = "overflow-hidden rounded-lg border bg-card";

/** Several changes of one run (same actor and channel, under 10 minutes apart), collapsible; expanded at first. */
export function RunGroup({ events }: { events: HistoryEvent[] }) {
  const [expanded, setExpanded] = useState(true);
  const id = useId();
  const actor = events[0]!.actor;
  return (
    <div className={card}>
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={id}
        onClick={() => setExpanded((x) => !x)}
        className={cn(
          "flex w-full items-center gap-3 bg-muted px-4 py-2.5 text-left outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:ring-inset pointer-coarse:min-h-11",
          expanded && "border-b",
        )}
      >
        <Avatar actor={actor}>{actor.kind === "agent" ? <Bot className="size-3.5" /> : undefined}</Avatar>
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-sm font-semibold">{displayName(actor)}</span>
          <span className="truncate text-xs text-muted-foreground tabular-nums">
            {channelPhrase(actor.channel)} · {timeRange(events)} · {events.length} Änderungen
          </span>
        </span>
        <ChevronDown
          aria-hidden
          className={cn("ml-auto size-4 shrink-0 text-muted-foreground transition-transform duration-150 motion-reduce:transition-none", expanded && "rotate-180")}
        />
      </button>
      <ul id={id} hidden={!expanded}>{events.map((e) => <HistoryEventRow key={e.id} event={e} />)}</ul>
    </div>
  );
}

/** A change on its own, in the same card frame as a run. */
export function SingleEvent({ event }: { event: HistoryEvent }) {
  return <ul className={card}><HistoryEventRow event={event} /></ul>;
}
